import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useHistory, useParams } from 'react-router-dom';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import { notification } from '@spark/ui-components/utils/notification';
import {
  createMotivoOuvidoria,
  getMotivoOuvidoriaById,
  updateMotivoOuvidoria,
} from '@spark/services';
import { formSections, formStructure, validateForm } from './formConfig';

const Forms = ({ isReadOnly = false }) => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [initialData, setInitialData] = useState(null);

  const isEditMode = !!id && id !== 'criar' && !isReadOnly;

  useEffect(() => {
    if (!(isReadOnly || isEditMode) || !id || id === 'criar') {
      return;
    }

    const load = async () => {
      try {
        setLoadingData(true);
        const data = await getMotivoOuvidoriaById(id);
        setInitialData({
          id: data?.id,
          nomeMotivo: data?.nomeMotivo || '',
          descricao: data?.descricao || '',
          dataCriacao: data?.dataCriacao || '',
        });
      } catch (error) {
        console.error('Erro ao carregar motivo da ouvidoria:', error);
        notification.error('Não foi possível carregar o motivo da ouvidoria.');
      } finally {
        setLoadingData(false);
      }
    };

    load();
  }, [id, isEditMode, isReadOnly]);

  const defaultValues = useMemo(
    () => ({
      nomeMotivo: '',
      descricao: '',
      dataCriacao: '',
    }),
    []
  );

  const initialValues = initialData || defaultValues;

  const buildComparablePayload = useCallback(values => ({
    nomeMotivo: values?.nomeMotivo?.trim() || '',
    descricao: values?.descricao?.trim() || '',
  }), []);

  const hasChanges = useCallback(
    values => {
      if (!isEditMode || !initialData) {
        return true;
      }

      const current = buildComparablePayload(values);
      const original = buildComparablePayload(initialData);
      return JSON.stringify(current) !== JSON.stringify(original);
    },
    [buildComparablePayload, initialData, isEditMode]
  );

  const onSubmit = useCallback(
    async values => {
      if (isEditMode && !hasChanges(values)) {
        notification.warning('Nenhuma informacao alterada');
        return;
      }

      try {
        setLoading(true);

        const payload = {
          id: initialData?.id || 0,
          nomeMotivo: values.nomeMotivo?.trim() || '',
          descricao: values.descricao?.trim() || '',
          dataCriacao: initialData?.dataCriacao || new Date().toISOString(),
        };

        if (isEditMode) {
          await updateMotivoOuvidoria(id, payload);
          notification.success('Motivo da ouvidoria atualizado com sucesso!');
        } else {
          await createMotivoOuvidoria(payload);
          notification.success('Motivo da ouvidoria criado com sucesso!');
        }

        history.push('/pages/motivoouvidoria');
      } catch (error) {
        console.error('Erro ao salvar motivo da ouvidoria:', error);
        notification.error('Erro ao salvar o motivo da ouvidoria. Tente novamente.');
      } finally {
        setLoading(false);
      }
    },
    [hasChanges, history, id, initialData?.dataCriacao, initialData?.id, isEditMode]
  );

  if ((isReadOnly || isEditMode) && loadingData) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: 'center', padding: 50 }}>
            Carregando dados do motivo da ouvidoria...
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Form onSubmit={onSubmit} initialValues={initialValues} validate={validateForm}>
      {({ handleSubmit }) => (
        <Card>
          <CardBody>
            <FormContainer onSubmit={handleSubmit}>
              <DynamicForm
                structure={formStructure}
                sections={formSections(initialValues)}
                isViewMode={isReadOnly}
                isEditing={isEditMode}
              />

              {!isReadOnly && (
                <FormButtonToolbar className="mt-5">
                  <ButtonWithLoading
                    variant="primary"
                    type="submit"
                    loading={loading ? 'true' : undefined}
                    disabled={loading}
                  >
                    {isEditMode ? 'Atualizar' : 'Salvar'}
                  </ButtonWithLoading>

                  <Button variant="secondary" type="button" onClick={() => history.goBack()}>
                    Cancelar
                  </Button>
                </FormButtonToolbar>
              )}
            </FormContainer>
          </CardBody>
        </Card>
      )}
    </Form>
  );
};

export default Forms;
